<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';

export default {
  name: "UploadResidentModal",
  data() {
    return {
      payload: {
        file: null,
      },
      fileName: null,
      errMsg: '',
      isUploading: false,
      condoId: this.$route.params.condoId,
    };
  },
  methods: {
    ...mapActions('condoMngt', ['uploadResident']),

    isValidFile(file) {
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      ];
      const validExtensions = ['.csv', '.xls', '.xlsx'];
      return (
        validTypes.includes(file.type) ||
        validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
      );
    },

    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        if (!this.isValidFile(file)) {
          this.errMsg = 'Only CSV or Excel files are allowed';
          this.payload.file = null;
          this.fileName = null;
          return;
        }
        this.errMsg = '';
        this.payload.file = file;
        this.fileName = file.name;
      }
    },

    handleDrop(e) {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file && this.isValidFile(file)) {
        this.errMsg = '';
        this.payload.file = file;
        this.fileName = file.name;
      } else {
        this.errMsg = 'Only CSV or Excel files are allowed';
      }
    },

    removeFile() {
      this.payload.file = null;
      this.fileName = null;
      this.$refs.fileInput.value = null;
    },

    async handleConfirm() {
      if (!this.payload.file) {
        useAlert('Please upload a CSV or Excel file before submitting.');
        this.errMsg = 'File is required';
        return;
      }

      this.isUploading = true;
      try {
        const formData = new FormData();
        formData.append('file', this.payload.file);

        await this.uploadResident({
          condoId: this.condoId,
          formData,
        });

        useAlert('Residents uploaded successfully.');
        this.$emit('close');
        this.$emit('refresh');
      } catch (err) {
        console.error(err);
        if (Array.isArray(err)) {
          useAlert(err.join('\n'));
        } else {
          useAlert('Failed to upload residents. Please try again.');
        }
      } finally {
        this.isUploading = false;
      }
    },
  },
};
</script>

<template>
  <div class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50">
    <div class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-96 max-h-[80vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">Upload Residents File</h1>
        <woot-button variant="clear" color-scheme="alert" icon="dismiss" @click="$emit('close')" />
      </div>

      <form @submit.prevent="handleConfirm">
        <label class="block mb-2 text-slate-700 dark:text-slate-200">Upload a file</label>
        <div
          class="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer 
                hover:border-blue-500 dark:hover:border-blue-400 
                transition mb-4 bg-white dark:bg-slate-700/50 
                border-slate-300 dark:border-slate-600"
          @dragover.prevent
          @drop="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".csv, .xls, .xlsx"
            class="hidden"
            @change="handleFileChange"
          />

          <div v-if="fileName" class="relative">
            <p class="text-slate-700 dark:text-slate-200 font-medium mb-2">{{ fileName }}</p>
            <woot-button 
              size="small" 
              icon="delete" 
              color-scheme="alert" 
              @click.stop="removeFile" 
              class="absolute top-0 right-0 hover:bg-red-300 dark:hover:bg-red-500/70" 
            />
          </div>

          <div v-else class="text-slate-500 dark:text-slate-400">
            <p>Drag & drop your CSV or Excel file here, or <span class="text-blue-600 dark:text-blue-400">browse</span></p>
            <p class="text-xs text-slate-400 dark:text-slate-500">Supported formats: .CSV, .XLS, .XLSX</p>
          </div>
        </div>

        <p v-if="errMsg" class="text-red-500 mb-2">{{ errMsg }}</p>
      </form>

      <!-- Action buttons -->
      <div class="flex justify-end gap-4 mt-4">
        <woot-button color-scheme="secondary" @click="$emit('close')">Cancel</woot-button>
        <woot-button :is-loading="isUploading" @click="handleConfirm">Submit</woot-button>
      </div>
    </div>
  </div>
</template>
